import React, { useState, useEffect, useCallback, useRef } from 'react';
import MainLayout from '../components/MainLayout';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import CTCGroupEditor from '../components/CTCGroupEditor';
import { toast } from 'sonner';
import { useActiveCompany } from '../hooks/useActiveCompany';
import { Link } from 'react-router-dom';
import { formatApiError } from '../lib/errorUtils';
import {
  Settings, Lock, Unlock, Calendar, Users, FileInput,
  Layers, Save, Plus, X, CheckCircle2, Send, Loader2, Copy,
  UserPlus, TrendingUp, UserMinus, Pause, Eye, Edit3, Trash2,
  Clock, Shield, ShieldAlert,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BusinessControls = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('overview');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [period, setPeriod] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [tenantConfig, setTenantConfig] = useState(null);
  const [periodControls, setPeriodControls] = useState([]);
  const [ctcGroups, setCtcGroups] = useState([]);
  const [salaryHeadMaster, setSalaryHeadMaster] = useState([]);
  // Statutory config for the active company — drives PF (15K cap) / ESI (21K cap)
  // logic inside the CTC Group live-preview engine. Falls back to defaults if
  // a company hasn't configured statutory rates yet.
  const [statutoryConfig, setStatutoryConfig] = useState({
    pf:  { wage_ceiling: 15000, employee_rate: 12,   employer_rate: 12,   is_active: true },
    esi: { wage_ceiling: 21000, employee_rate: 0.75, employer_rate: 3.25, is_active: true },
  });
  const [inputGrid, setInputGrid] = useState(null);
  const [attConfig, setAttConfig] = useState(null);
  const [attRecon, setAttRecon] = useState(null);
  const [reconciliationReport, setReconciliationReport] = useState(null);
  const [exceptionLogs, setExceptionLogs] = useState([]);
  const [saving, setSaving] = useState(false);
  // CTC modals
  const [showNewGroup, setShowNewGroup] = useState(false);
  // When non-null, the editor opens in EDIT mode pre-populated with this group.
  // We reshape stored fields back to the editor's draft shape on open.
  const [editingGroup, setEditingGroup] = useState(null);
  const [showCopyGroup, setShowCopyGroup] = useState(false);
  const [copySource, setCopySource] = useState(null);
  const [newGroup, setNewGroup] = useState({
    name: '', code: '', description: '', effective_from: '',
    applicable_grades: [], salary_heads: [],
    include_food: false, include_gratuity: false, include_telephone: false, include_mobile: false,
    include_internet: false, include_conveyance: false, include_driver: false, include_books: false,
    include_meal_card: false, include_health_insurance: false, include_lta: false,
    custom_perks: [],
    employee_ids: [],
  });
  const [copyForm, setCopyForm] = useState({ new_code: '', new_name: '', effective_from: '' });
  // Period preview modal
  const [previewPeriod, setPreviewPeriod] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Input grid filter
  const [inputFilter, setInputFilter] = useState('all'); // all, joinee, increment, resignee, hold
  // Auto-detect calc_method modal
  const [showAutoDetect, setShowAutoDetect] = useState(false);
  const [autoDetectLoading, setAutoDetectLoading] = useState(false);
  const [autoDetectResult, setAutoDetectResult] = useState(null);
  const [autoDetectOverwrite, setAutoDetectOverwrite] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/companies`, { withCredentials: true }).then(res => {
      setCompanies(res.data);
      setSelectedCompany((cur) => (res.data.length && !cur ? res.data[0].id : cur));
    });
  }, [setSelectedCompany]);

  const fetchTab = useCallback(async () => {
    if (!selectedCompany && tab !== 'overview' && tab !== 'tenant') return;
    setLoading(true);
    try {
      if (tab === 'overview') {
        const [dash, logs] = await Promise.all([
          axios.get(`${API_URL}/controls/dashboard`, { params: { company_id: selectedCompany }, withCredentials: true }),
          axios.get(`${API_URL}/controls/exception-logs`, { params: { company_id: selectedCompany, limit: 20 }, withCredentials: true }),
        ]);
        setDashboard(dash.data); setExceptionLogs(logs.data);
      } else if (tab === 'tenant') {
        const { data } = await axios.get(`${API_URL}/controls/tenant-config`, { withCredentials: true });
        setTenantConfig(data);
      } else if (tab === 'period') {
        const { data } = await axios.get(`${API_URL}/controls/periods`, { params: { company_id: selectedCompany }, withCredentials: true });
        setPeriodControls(data);
      } else if (tab === 'ctc') {
        const [grp, heads, stat] = await Promise.all([
          axios.get(`${API_URL}/controls/ctc-groups`, { params: { company_id: selectedCompany }, withCredentials: true }),
          axios.get(`${API_URL}/masters/salary-heads`, { params: { company_id: selectedCompany }, withCredentials: true }),
          axios.get(`${API_URL}/payroll/statutory`, { params: { company_id: selectedCompany }, withCredentials: true }),
        ]);
        setCtcGroups(grp.data);
        setSalaryHeadMaster(heads.data || []);
        // Merge fetched stat over defaults so missing keys keep the 15K / 21K caps.
        setStatutoryConfig(prev => ({
          pf:  { ...prev.pf,  ...(stat.data?.pf  || {}) },
          esi: { ...prev.esi, ...(stat.data?.esi || {}) },
        }));
      } else if (tab === 'inputs') {
        const { data } = await axios.get(`${API_URL}/controls/monthly-inputs/grid`, { params: { company_id: selectedCompany, period }, withCredentials: true });
        setInputGrid(data);
        // Sync dynamic salary head definitions so the grid and save payload both
        // include any custom heads configured in Masters → Salary Heads.
        setInputDynHeadDefs(data.dynamic_head_defs || []);
      } else if (tab === 'attendance') {
        const [cfg, recon] = await Promise.all([
          axios.get(`${API_URL}/controls/attendance-config`, { params: { company_id: selectedCompany }, withCredentials: true }),
          axios.get(`${API_URL}/controls/attendance/reconciliation`, { params: { company_id: selectedCompany, period }, withCredentials: true }),
        ]);
        setAttConfig(cfg.data); setAttRecon(recon.data);
      } else if (tab === 'reconciliation') {
        const { data } = await axios.get(`${API_URL}/controls/reconciliation-report`, { params: { company_id: selectedCompany, period }, withCredentials: true });
        setReconciliationReport(data);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [tab, selectedCompany, period]);

  useEffect(() => { fetchTab(); }, [fetchTab]);

  const saveTenantConfig = async () => {
    setSaving(true);
    try { await axios.post(`${API_URL}/controls/tenant-config`, tenantConfig, { withCredentials: true }); toast.success('Tenant config saved'); }
    catch (err) { toast.error(formatApiError(err) || 'Save failed'); }
    finally { setSaving(false); }
  };

  const doPeriodAction = async (periodId, action, reason = '') => {
    try { await axios.post(`${API_URL}/controls/periods/${periodId}/action`, { action, reason: reason || action }, { withCredentials: true }); toast.success(`Period ${action} done`); fetchTab(); }
    catch (err) { toast.error(formatApiError(err) || 'Failed'); }
  };

  const openPeriodPreview = async (periodId) => {
    setPreviewLoading(true);
    setPreviewPeriod({ id: periodId });
    try {
      const { data } = await axios.get(`${API_URL}/controls/periods/${periodId}/preview`, { withCredentials: true });
      setPreviewPeriod(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load preview');
      setPreviewPeriod(null);
    } finally { setPreviewLoading(false); }
  };

  const saveAttConfig = async () => {
    setSaving(true);
    try { await axios.post(`${API_URL}/controls/attendance-config`, { ...attConfig, company_id: selectedCompany }, { withCredentials: true }); toast.success('Attendance config saved'); }
    catch (err) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };
  const doAttLock = async (action) => {
    try {
      const url = action === 'lock' ? `${API_URL}/controls/attendance/lock-period` : `${API_URL}/controls/attendance/unlock-period`;
      await axios.post(url, null, { params: { company_id: selectedCompany, period, reason: `${action} by admin` }, withCredentials: true });
      toast.success(`Attendance ${action}ed`); fetchTab();
    } catch (err) { toast.error(formatApiError(err) || 'Failed'); }
  };

  // ===== CTC GROUP ACTIONS =====
  // Used for both Create and Edit — `editingGroup` decides which API verb to use.
  const handleCreateGroup = async (payload) => {
    if (!payload.name || !payload.code || !payload.effective_from) {
      toast.error('Name, Code and Effective Date are required'); return;
    }
    setSaving(true);
    try {
      if (editingGroup) {
        // EDIT: PATCH the existing group. We strip company_id (immutable) so a
        // tenant can't accidentally move a group between companies via the form.
        const { company_id: _ignored, ...rest } = payload;
        await axios.patch(
          `${API_URL}/controls/ctc-groups/${editingGroup.id}`,
          rest,
          { withCredentials: true },
        );
        toast.success(`CTC Group "${payload.name}" updated`);
      } else {
        await axios.post(
          `${API_URL}/controls/ctc-groups`,
          { ...payload, company_id: selectedCompany },
          { withCredentials: true },
        );
        toast.success('CTC Group created');
      }
      setShowNewGroup(false);
      setEditingGroup(null);
      fetchTab();
    } catch (err) { toast.error(formatApiError(err) || 'Save failed'); }
    finally { setSaving(false); }
  };

  // Delete with employee-assignment guard. The backend rejects deletion when
  // employees are still mapped; we surface that message verbatim.
  const handleDeleteGroup = async (g) => {
    if (!window.confirm(
      `Delete CTC Group "${g.name}" (${g.code})?\n\nThis cannot be undone. ` +
      `Any salary-structure mirror will also be removed.\n\n` +
      `If employees are still assigned, the delete will be blocked — ` +
      `reassign or unassign them first.`
    )) return;
    try {
      await axios.delete(`${API_URL}/controls/ctc-groups/${g.id}`, { withCredentials: true });
      toast.success(`Deleted "${g.name}"`);
      fetchTab();
    } catch (err) { toast.error(formatApiError(err) || 'Delete failed'); }
  };

  // Open editor pre-populated. The list-API shape mostly matches the editor's
  // draft shape, but `salary_heads` rows may be missing the empty-string
  // defaults the editor expects — normalise here so inputs don't show "undefined".
  const openEditGroup = (g) => {
    const heads = (g.salary_heads || []).map(h => ({
      code: (h.code || h.head_code || '').toUpperCase(),
      name: h.name || h.head_name || h.code || '',
      type: h.type || h.head_type || 'earning',
      calc: h.calc || h.calculation_type || 'fixed',
      pct_of: (h.pct_of || h.percentage_of || 'CTC').toUpperCase(),
      pct_val: h.pct_val ?? h.percentage_value ?? '',
      value: h.value ?? '',
      ctc_component: h.ctc_component !== false,
    }));
    setEditingGroup({ ...g, salary_heads: heads });
    setShowNewGroup(true);
    setShowCopyGroup(false);
  };

  const handleCopyGroup = async () => {
    if (!copyForm.new_code || !copyForm.new_name || !copyForm.effective_from) { toast.error('All fields required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/controls/ctc-groups/${copySource.id}/copy`, null, {
        params: { new_code: copyForm.new_code, new_name: copyForm.new_name, effective_from: copyForm.effective_from },
        withCredentials: true,
      });
      toast.success(`Copied from "${copySource.name}"`);
      setShowCopyGroup(false); setCopySource(null);
      setCopyForm({ new_code: '', new_name: '', effective_from: '' });
      fetchTab();
    } catch (err) { toast.error(formatApiError(err) || 'Copy failed'); }
    finally { setSaving(false); }
  };

  // ===== INPUT GRID ACTIONS =====
  const [inputFields, setInputFields] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const inputFileRef = useRef(null);
  // Dynamic salary heads that have is_monthly_input=True — mirrors MonthlyInput.js dynHeadDefs.
  // Populated when the inputs tab grid is fetched so the grid shows the same
  // custom columns as the Payroll → Monthly Inputs page.
  const [inputDynHeadDefs, setInputDynHeadDefs] = useState([]);

  const updateInputRow = (empId, field, val) => {
    if (!inputGrid) return;
    // Support nested path updates for dynamic_heads: field = 'dynamic_heads.HEADCODE'
    if (field.startsWith('dynamic_heads.')) {
      const headCode = field.slice('dynamic_heads.'.length);
      setInputGrid({
        ...inputGrid,
        rows: inputGrid.rows.map(r =>
          r.employee_id === empId
            ? { ...r, dynamic_heads: { ...(r.dynamic_heads || {}), [headCode]: val } }
            : r
        ),
      });
      return;
    }
    setInputGrid({
      ...inputGrid,
      rows: inputGrid.rows.map(r => r.employee_id === empId ? { ...r, [field]: val } : r),
    });
  };
  const saveInputGrid = async () => {
    if (!inputGrid) return;
    setSaving(true);
    try {
      const rows = inputGrid.rows.map(r => {
        // Helper: coerce optional number field — blank/null → null, number → float.
        const optNum = (v) => (v === '' || v == null) ? null : (isNaN(Number(v)) ? null : Number(v));
        const row = {
          ...r,
          employee_id: r.employee_id,
          ot_hours: r.ot_hours || 0,
          ot_amount: r.ot_amount || 0,
          incentive: r.incentive || 0,
          performance_incentive: r.performance_incentive || 0,
          bonus: r.bonus || 0,
          variable_earning: r.variable_earning || 0,
          variable_pay: r.variable_pay || 0,
          arrears: r.arrears || 0,
          basic_arrear: r.basic_arrear || 0,
          hra_arrear: r.hra_arrear || 0,
          conveyance_arrear: r.conveyance_arrear || 0,
          relocation_arrears: r.relocation_arrears || 0,
          medical_arrear: r.medical_arrear || 0,
          special_allowance_arrear: r.special_allowance_arrear || 0,
          other_allowance: r.other_allowance || 0,
          reimbursement: r.reimbursement || 0,
          other_earning: r.other_earning || 0,
          other_deduction: r.other_deduction || 0,
          pt_arrear: r.pt_arrear || 0,
          esi_arrear: r.esi_arrear || 0,
          pf_arrear: r.pf_arrear || 0,
          lwf: r.lwf || 0,
          salary_advance_recovery: r.salary_advance_recovery || 0,
          advance_recovery: r.advance_recovery || 0,
          notice_pay_recovery: r.notice_pay_recovery || 0,
          hold_salary: r.hold_salary || false,
          remarks: r.remarks || '',
          lop_days_override: optNum(r.lop_days_override),
          additional_days: optNum(r.additional_days),
          base_days_override: optNum(r.base_days_override),
          tds_override: optNum(r.tds_override),
        };
        // Dynamic salary heads — include all defined heads as a sub-document.
        if (inputDynHeadDefs.length > 0) {
          row.dynamic_heads = Object.fromEntries(
            inputDynHeadDefs.map(hd => [
              hd.code,
              Number((r.dynamic_heads || {})[hd.code]) || 0,
            ])
          );
        }
        return row;
      });
      await axios.post(`${API_URL}/controls/monthly-inputs/grid`, { company_id: selectedCompany, period, rows }, { withCredentials: true });
      toast.success('Input grid saved — Payroll Monthly Inputs updated automatically');
      fetchTab();
    } catch (err) { toast.error(formatApiError(err) || 'Save failed'); }
    finally { setSaving(false); }
  };
  const doInputAction = async (action, remarks = '') => {
    try {
      await axios.post(`${API_URL}/controls/monthly-inputs/action`, { action, remarks }, { params: { company_id: selectedCompany, period }, withCredentials: true });
      toast.success(`Inputs ${action}: success`); fetchTab();
    } catch (err) { toast.error(formatApiError(err) || 'Action failed'); }
  };

  const fetchInputFields = async () => {
    if (!selectedCompany) return;
    try {
      const { data } = await axios.get(`${API_URL}/controls/monthly-input-fields`, { params: { company_id: selectedCompany }, withCredentials: true });
      setInputFields(data);
    } catch (err) { console.error(err); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/controls/monthly-inputs/template/download`, {
        params: { company_id: selectedCompany, period },
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `monthly_input_${period}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (err) { toast.error('Download failed'); }
  };

  const handleUploadTemplate = async (file) => {
    if (!file) return;
    setUploadingFile(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(
        `${API_URL}/controls/monthly-inputs/template/upload?company_id=${selectedCompany}&period=${period}`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setUploadResult(data);
      if (data.success_count > 0) { toast.success(`${data.success_count} rows uploaded`); fetchTab(); }
      if (data.error_count > 0) toast.error(`${data.error_count} rows had errors`);
    } catch (err) { toast.error(formatApiError(err) || 'Upload failed'); }
    finally { setUploadingFile(false); }
  };

  const handleBulkApprove = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/controls/monthly-inputs/approve-bulk`, null, {
        params: { company_id: selectedCompany, period },
        withCredentials: true,
      });
      toast.success(data.message); fetchTab();
    } catch (err) { toast.error(formatApiError(err) || 'Bulk approve failed'); }
  };

  const handleIndividualApprove = async (empId) => {
    try {
      const { data } = await axios.post(`${API_URL}/controls/monthly-inputs/approve/${empId}`, null, {
        params: { company_id: selectedCompany, period },
        withCredentials: true,
      });
      toast.success(data.message); fetchTab();
    } catch (err) { toast.error(formatApiError(err) || 'Approve failed'); }
  };

  const filteredRows = inputGrid?.rows?.filter(r => {
    if (inputFilter === 'joinee') return r.is_new_joinee;
    if (inputFilter === 'increment') return r.has_increment;
    if (inputFilter === 'resignee') return r.is_resignee;
    if (inputFilter === 'hold') return r.hold_salary;
    return true;
  }) || [];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Eye },
    { id: 'tenant', label: 'Tenant Config', icon: Settings },
    { id: 'period', label: 'Period Control', icon: Lock },
    { id: 'ctc', label: 'Salary Structures (CTC Groups)', icon: Layers },
    { id: 'inputs', label: 'Monthly Inputs', icon: FileInput },
    { id: 'attendance', label: 'Attendance', icon: Clock },
    { id: 'reconciliation', label: 'Module Reconciliation', icon: ShieldAlert },
  ];

  const SC = ({ label, value, sub, color = 'blue' }) => (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`mt-1 text-xl font-semibold font-mono text-${color}-600`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );

  return (
    <MainLayout>
      <div className="space-y-5" data-testid="business-controls-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Business Controls</h1>
            <p className="mt-1 text-sm text-slate-500">Tenant, Period, CTC, Monthly Input, Attendance · <Link to="/payroll/setup" className="text-blue-600 hover:underline">Calendar moved to Payroll Setup →</Link></p>
          </div>
          <Shield className="w-8 h-8 text-slate-400" />
        </div>

        {/* Controls Bar */}
        <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap gap-3 items-end" data-testid="controls-bar">
          <div className="min-w-[180px]">
            <label className="block text-[10px] font-medium text-slate-500 mb-1">Company</label>
            <select data-testid="controls-company-select" value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)}
              className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm">
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {['inputs', 'attendance'].includes(tab) && (
            <div className="min-w-[140px]">
              <label className="block text-[10px] font-medium text-slate-500 mb-1">Period</label>
              <input data-testid="controls-period-input" type="month" value={period} onChange={e => setPeriod(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm" />
            </div>
          )}
        </div>

        {/* Tab Bar */}
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 overflow-x-auto" data-testid="controls-tabs">
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} data-testid={`controls-tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium whitespace-nowrap transition-all ${tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <>
            {/* ====== OVERVIEW ====== */}
            {tab === 'overview' && dashboard && (
              <div className="space-y-5" data-testid="controls-overview">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  <SC label="Modules" value={dashboard.tenant_config.modules_enabled} sub="enabled" />
                  <SC label="Open Periods" value={dashboard.period_controls.open_periods} sub={`of ${dashboard.period_controls.total_periods || 0} total`} color="green" />
                  <SC label="Locked Periods" value={dashboard.period_controls.locked_periods} color="red" />
                  <SC label="Pending Inputs" value={dashboard.monthly_inputs.pending_approval} sub={`${dashboard.monthly_inputs.drafts || 0} drafts · ${dashboard.monthly_inputs.approved || 0} approved`} color="amber" />
                  <SC label="CTC Groups" value={dashboard.ctc_groups} color="purple" />
                  <SC label="Calendars" value={dashboard.calendar_configs || 0} color="blue" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <SC label="Att. Exceptions" value={dashboard.attendance.open_exceptions} color="red" />
                  <SC label="Submitted MI" value={dashboard.monthly_inputs.submitted || 0} color="amber" />
                  <SC label="Maker-Checker" value={dashboard.tenant_config.maker_checker ? 'On' : 'Off'} color={dashboard.tenant_config.maker_checker ? 'green' : 'amber'} />
                  <SC label="Exceptions (24h)" value={dashboard.exception_logs_24h || 0} color="red" />
                </div>
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <h3 className="font-semibold text-sm text-slate-800 mb-3">Recent Exception Logs</h3>
                  {exceptionLogs.length > 0 ? (
                    <div className="space-y-1 max-h-[200px] overflow-y-auto" data-testid="exception-logs-list">
                      {exceptionLogs.map((l, i) => (
                        <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-slate-50">
                          <div className="flex items-center gap-2">
                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${l.type?.includes('unlock') ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>{l.type}</span>
                            <span className="text-slate-600">{l.action} — {l.reason || 'N/A'}</span>
                          </div>
                          <span className="text-slate-400 font-mono text-[10px]">{l.timestamp?.slice(0, 16)}</span>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-xs text-slate-400">No recent logs</p>}
                </div>
              </div>
            )}

            {/* ====== TENANT CONFIG ====== */}
            {tab === 'tenant' && tenantConfig && (
              <div className="space-y-4" data-testid="controls-tenant">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sm text-slate-800">Tenant-Level Configuration</h3>
                  <button data-testid="tenant-save-btn" onClick={saveTenantConfig} disabled={saving}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-medium disabled:opacity-50">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
                    <h4 className="text-xs font-semibold text-slate-700 uppercase">Modules</h4>
                    {['payroll', 'attendance', 'leave', 'statutory', 'ess', 'reports', 'finance', 'controls'].map(m => (
                      <label key={m} className="flex items-center gap-2 text-xs text-slate-700">
                        <input type="checkbox" checked={tenantConfig.modules_enabled?.includes(m)}
                          onChange={e => { const mods = tenantConfig.modules_enabled || []; setTenantConfig({ ...tenantConfig, modules_enabled: e.target.checked ? [...mods, m] : mods.filter(x => x !== m) }); }}
                          className="rounded border-slate-300" data-testid={`tenant-module-${m}`} />
                        {m.charAt(0).toUpperCase() + m.slice(1)}
                      </label>
                    ))}
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
                    <h4 className="text-xs font-semibold text-slate-700 uppercase">Controls</h4>
                    {[{k:'force_maker_checker',l:'Force Maker-Checker'},{k:'allow_period_reopen',l:'Allow Period Reopen'},{k:'attendance_mandatory',l:'Attendance Mandatory'},{k:'input_approval_required',l:'Input Approval Required'},{k:'statutory_auto_compute',l:'Auto Statutory Compute'}].map(({k,l}) => (
                      <label key={k} className="flex items-center gap-2 text-xs text-slate-700">
                        <input type="checkbox" checked={tenantConfig[k]||false} onChange={e=>setTenantConfig({...tenantConfig,[k]:e.target.checked})} className="rounded border-slate-300" data-testid={`tenant-${k}`} /> {l}
                      </label>
                    ))}
                    <div className="flex items-center gap-2 pt-2">
                      <label className="text-xs text-slate-600 w-36">Max Reopen Count</label>
                      <input type="number" value={tenantConfig.max_reopen_count||3} min={0} max={10} onChange={e=>setTenantConfig({...tenantConfig,max_reopen_count:parseInt(e.target.value)})} className="w-20 border border-slate-300 rounded px-2 py-1 text-xs" data-testid="tenant-max-reopen" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ====== PERIOD CONTROL ====== */}
            {tab === 'period' && (
              <div className="space-y-4" data-testid="controls-period">
                <h3 className="font-semibold text-sm text-slate-800">Period Lock / Unlock / Reopen</h3>
                <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
                  <table className="w-full text-xs" data-testid="period-control-table">
                    <thead><tr className="bg-slate-50 border-b">
                      <th className="px-3 py-2 text-left text-slate-600">Period</th><th className="px-3 py-2 text-left text-slate-600">Status</th>
                      <th className="px-3 py-2 text-center text-slate-600">Att</th><th className="px-3 py-2 text-center text-slate-600">Inputs</th>
                      <th className="px-3 py-2 text-center text-slate-600">Payroll</th><th className="px-3 py-2 text-center text-slate-600">Reopens</th>
                      <th className="px-3 py-2 text-right text-slate-600">Actions</th>
                    </tr></thead>
                    <tbody>
                      {periodControls.map(p => { const c=p.controls||{}; return (
                        <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-3 py-2 font-medium text-slate-900">{p.period||p.period_name}</td>
                          <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${p.status==='locked'?'bg-red-50 text-red-700':p.status==='open'?'bg-green-50 text-green-700':'bg-amber-50 text-amber-700'}`}>{p.status}</span></td>
                          <td className="px-3 py-2 text-center">{c.attendance_locked?<Lock className="w-3.5 h-3.5 text-red-500 mx-auto"/>:<Unlock className="w-3.5 h-3.5 text-green-500 mx-auto"/>}</td>
                          <td className="px-3 py-2 text-center">{c.inputs_locked?<Lock className="w-3.5 h-3.5 text-red-500 mx-auto"/>:<Unlock className="w-3.5 h-3.5 text-green-500 mx-auto"/>}</td>
                          <td className="px-3 py-2 text-center">{c.payroll_locked?<Lock className="w-3.5 h-3.5 text-red-500 mx-auto"/>:<Unlock className="w-3.5 h-3.5 text-green-500 mx-auto"/>}</td>
                          <td className="px-3 py-2 text-center font-mono">{c.reopen_count||0}</td>
                          <td className="px-3 py-2 text-right"><div className="flex gap-1 justify-end">
                            <button
                              data-testid={`period-preview-${p.id}`}
                              onClick={() => openPeriodPreview(p.id)}
                              className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-[10px] font-medium flex items-center gap-1"
                              title="Preview period summary"
                            ><Eye className="w-3 h-3" /> Preview</button>
                            {!c.attendance_locked&&!c.inputs_locked&&<button data-testid={`period-freeze-${p.id}`} onClick={()=>doPeriodAction(p.id,'freeze_inputs')} className="px-2 py-1 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded text-[10px] font-medium">Freeze</button>}
                            {!c.payroll_locked&&<button data-testid={`period-lock-${p.id}`} onClick={()=>doPeriodAction(p.id,'lock')} className="px-2 py-1 bg-red-100 hover:bg-red-200 text-red-800 rounded text-[10px] font-medium">Lock</button>}
                            {c.payroll_locked&&<button data-testid={`period-reopen-${p.id}`} onClick={()=>doPeriodAction(p.id,'reopen','Reopen')} className="px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded text-[10px] font-medium">Reopen</button>}
                          </div></td>
                        </tr>); })}
                    </tbody>
                  </table>
                  {periodControls.length===0&&<p className="text-xs text-slate-400 text-center py-6">No periods found</p>}
                </div>

                {/* Period Preview Modal */}
                {previewPeriod && (
                  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPreviewPeriod(null)}>
                    <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-5 space-y-3 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="period-preview-modal">
                      <div className="flex items-center justify-between border-b pb-2">
                        <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Eye className="w-4 h-4 text-slate-600" /> Period Preview</h3>
                        <button onClick={() => setPreviewPeriod(null)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
                      </div>
                      {previewLoading ? (
                        <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
                      ) : previewPeriod.period ? (
                        <div className="space-y-3 text-xs">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <SC label="Period" value={previewPeriod.period_name || previewPeriod.period} />
                            <SC label="Status" value={previewPeriod.status} color={previewPeriod.status === 'locked' ? 'red' : previewPeriod.status === 'frozen' ? 'amber' : 'green'} />
                            <SC label="Active Employees" value={previewPeriod.employees_active} />
                            <SC label="Attendance Rows" value={previewPeriod.attendance_rows} />
                            <SC label="Payroll Runs" value={previewPeriod.payroll_runs_total} color="purple" />
                            <SC label="Total Gross" value={`₹${(previewPeriod.total_gross || 0).toLocaleString('en-IN')}`} color="green" />
                            <SC label="Total Net" value={`₹${(previewPeriod.total_net || 0).toLocaleString('en-IN')}`} color="green" />
                            <SC label="Last Processed" value={previewPeriod.last_processed_at ? previewPeriod.last_processed_at.slice(0, 16).replace('T', ' ') : 'Never'} />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="bg-slate-50 rounded-lg p-3" data-testid="preview-mi-stats">
                              <p className="text-[11px] font-semibold text-slate-700 uppercase mb-1">Monthly Inputs by Status</p>
                              {Object.keys(previewPeriod.monthly_inputs_by_status || {}).length === 0 ? (
                                <p className="text-slate-400">No monthly inputs for this period.</p>
                              ) : Object.entries(previewPeriod.monthly_inputs_by_status).map(([k, v]) => (
                                <div key={k} className="flex justify-between py-0.5"><span className="capitalize">{k}</span><span className="font-mono font-semibold">{v}</span></div>
                              ))}
                            </div>
                            <div className="bg-slate-50 rounded-lg p-3" data-testid="preview-runs-stats">
                              <p className="text-[11px] font-semibold text-slate-700 uppercase mb-1">Payroll Runs by Status</p>
                              {Object.keys(previewPeriod.payroll_runs_by_status || {}).length === 0 ? (
                                <p className="text-slate-400">No payroll runs yet.</p>
                              ) : Object.entries(previewPeriod.payroll_runs_by_status).map(([k, v]) => (
                                <div key={k} className="flex justify-between py-0.5"><span className="capitalize">{k.replace(/_/g, ' ')}</span><span className="font-mono font-semibold">{v}</span></div>
                              ))}
                            </div>
                          </div>
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-blue-900">
                            <p className="font-semibold mb-1">Lock state</p>
                            <p>Attendance: <strong>{previewPeriod.controls?.attendance_locked ? 'Locked' : 'Open'}</strong> · Inputs: <strong>{previewPeriod.controls?.inputs_locked ? 'Locked' : 'Open'}</strong> · Payroll: <strong>{previewPeriod.controls?.payroll_locked ? 'Locked' : 'Open'}</strong> · Reopens: <strong>{previewPeriod.controls?.reopen_count || 0}</strong></p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 text-center py-6">No data</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ====== CTC GROUPS (REDESIGNED) ====== */}
            {tab === 'ctc' && (
              <div className="space-y-4" data-testid="controls-ctc">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sm text-slate-800">Multi-Group CTC Setup</h3>
                  <div className="flex gap-2">
                    <button data-testid="ctc-base-days-template-btn"
                      onClick={async () => {
                        try {
                          const res = await axios.get(`${API_URL}/controls/ctc-groups/base-days/template`,
                            { withCredentials: true, responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([res.data]));
                          const a = document.createElement('a');
                          a.href = url; a.download = 'base_days_template.csv'; a.click();
                          window.URL.revokeObjectURL(url);
                        } catch (e) { toast.error('Template download failed'); }
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-xs font-medium">
                      📅 Base Days Template
                    </button>
                    <label className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-md text-xs font-medium cursor-pointer"
                      data-testid="ctc-base-days-upload-btn">
                      📥 Bulk Update Base Days
                      <input type="file" accept=".csv" className="hidden"
                        onChange={async (e) => {
                          const f = e.target.files?.[0]; if (!f) return;
                          const fd = new FormData(); fd.append('file', f);
                          try {
                            const { data } = await axios.post(
                              `${API_URL}/controls/ctc-groups/base-days/bulk-upload?company_id=${selectedCompany}`,
                              fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
                            );
                            if (data.errors?.length) toast.warning(`Updated ${data.updated} group(s) — ${data.errors.length} error(s)`);
                            else toast.success(`Updated ${data.updated} group(s)`);
                            fetchTab();
                          } catch (err) { toast.error(formatApiError(err) || 'Upload failed'); }
                          e.target.value = '';
                        }} />
                    </label>
                    <button data-testid="ctc-hierarchy-export-btn"
                      onClick={async () => {
                        try {
                          const res = await axios.get(
                            `${API_URL}/controls/ctc-groups/hierarchy/export?company_id=${selectedCompany}`,
                            { withCredentials: true, responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([res.data]));
                          const a = document.createElement('a');
                          a.href = url; a.download = 'ctc_group_hierarchy.csv'; a.click();
                          window.URL.revokeObjectURL(url);
                          toast.success('Hierarchy exported — edit & re-upload');
                        } catch (e) { toast.error('Export failed'); }
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-100 hover:bg-violet-200 text-violet-700 rounded-md text-xs font-medium">
                      🌳 Export Parent / Sub / Cycle
                    </button>
                    <label className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-md text-xs font-medium cursor-pointer"
                      data-testid="ctc-hierarchy-upload-btn"
                      title="Bulk-map Parent group, Sub-group and Pay Cycle for many CTC groups at once">
                      📤 Bulk Map Hierarchy + Cycle
                      <input type="file" accept=".csv" className="hidden"
                        onChange={async (e) => {
                          const f = e.target.files?.[0]; if (!f) return;
                          const fd = new FormData(); fd.append('file', f);
                          try {
                            const { data } = await axios.post(
                              `${API_URL}/controls/ctc-groups/hierarchy/bulk-upload?company_id=${selectedCompany}`,
                              fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
                            );
                            if (data.errors?.length) toast.warning(`Mapped ${data.updated} · ${data.errors.length} error(s) — first: ${data.errors[0]?.error}`);
                            else toast.success(`Mapped ${data.updated} group(s) to parent/sub + pay cycle`);
                            fetchTab();
                          } catch (err) { toast.error(formatApiError(err) || 'Upload failed'); }
                          e.target.value = '';
                        }} />
                    </label>
                    <button data-testid="ctc-auto-detect-btn"
                      onClick={() => setShowAutoDetect(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-md text-xs font-medium"
                      title="Auto-suggest Staff (CTC) vs O&M (Per-Day Wage) calc method based on group code/name keywords">
                      🪄 Auto-Detect Methods
                    </button>
                    <button data-testid="ctc-add-new-btn" onClick={() => { setShowNewGroup(true); setShowCopyGroup(false); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-medium">
                      <Plus className="w-3.5 h-3.5" /> Add New Group
                    </button>
                  </div>
                </div>

                {/* F&F Salary Proration Method Group-wise Matrix */}
                <div className="bg-indigo-50/80 border border-indigo-200 rounded-lg p-4 space-y-3" data-testid="fnf-proration-matrix-panel">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-sm text-indigo-950 flex items-center gap-2">
                        🏷️ F&F Salary Calculation Settings (Group Wise)
                      </h4>
                      <p className="text-xs text-indigo-700 mt-0.5">
                        Configure whether partial-month F&F settlement salary is prorated using <b>Base Days</b> (Calendar Month) or <b>Wage Days</b> (Configured Wage Cycle). Employees automatically inherit this from their Payroll Group.
                      </p>
                    </div>
                  </div>
                  {ctcGroups.length === 0 ? (
                    <p className="text-xs text-slate-400">No Payroll / CTC Groups configured yet.</p>
                  ) : (
                    <div className="bg-white rounded-md border border-indigo-200 overflow-x-auto">
                      <table className="w-full text-xs text-left" data-testid="fnf-proration-matrix-table">
                        <thead className="bg-indigo-100/60 text-indigo-900 font-semibold border-b border-indigo-200">
                          <tr>
                            <th className="px-3 py-2">Payroll Group</th>
                            <th className="px-3 py-2">Code</th>
                            <th className="px-3 py-2">Assigned Employees</th>
                            <th className="px-3 py-2 text-center">F&F Salary Calculation Method</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-indigo-100">
                          {ctcGroups.map((g) => {
                            const curMethod = g.salary_proration_method === 'BASE_DAYS' ? 'BASE_DAYS' : 'WAGE_DAYS';
                            return (
                              <tr key={g.id} className="hover:bg-indigo-50/40">
                                <td className="px-3 py-2 font-semibold text-slate-800">{g.name}</td>
                                <td className="px-3 py-2 font-mono text-slate-500">{g.code}</td>
                                <td className="px-3 py-2 text-slate-600">{g.employee_count || 0} employees</td>
                                <td className="px-3 py-2 text-center">
                                  <div className="flex items-center justify-center gap-4">
                                    <label className="flex items-center gap-1.5 cursor-pointer text-xs font-medium text-slate-700">
                                      <input
                                        type="radio"
                                        name={`fnf_method_${g.id}`}
                                        value="BASE_DAYS"
                                        checked={curMethod === 'BASE_DAYS'}
                                        onChange={async () => {
                                          try {
                                            const { company_id: _, ...rest } = g;
                                            await axios.patch(`${API_URL}/controls/ctc-groups/${g.id}`, { ...rest, salary_proration_method: 'BASE_DAYS' }, { withCredentials: true });
                                            toast.success(`Updated ${g.name} to Base Days`);
                                            fetchTab();
                                          } catch (err) { toast.error('Update failed'); }
                                        }}
                                        className="text-indigo-600 focus:ring-indigo-500"
                                      />
                                      Base Days (Calendar Month)
                                    </label>
                                    <label className="flex items-center gap-1.5 cursor-pointer text-xs font-medium text-slate-700">
                                      <input
                                        type="radio"
                                        name={`fnf_method_${g.id}`}
                                        value="WAGE_DAYS"
                                        checked={curMethod === 'WAGE_DAYS'}
                                        onChange={async () => {
                                          try {
                                            const { company_id: _, ...rest } = g;
                                            await axios.patch(`${API_URL}/controls/ctc-groups/${g.id}`, { ...rest, salary_proration_method: 'WAGE_DAYS' }, { withCredentials: true });
                                            toast.success(`Updated ${g.name} to Wage Days`);
                                            fetchTab();
                                          } catch (err) { toast.error('Update failed'); }
                                        }}
                                        className="text-blue-600 focus:ring-blue-500"
                                      />
                                      Wage Days (Wage Cycle)
                                    </label>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Existing Groups */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ctc-groups-grid">
                  {ctcGroups.map(g => (
                    <div key={g.id} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`ctc-group-${g.code}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-sm text-slate-900">{g.name}</h4>
                          <span className="text-[10px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{g.code}</span>
                        </div>
                        <div className="flex gap-1">
                          <Link to={`/employees?ctc_group=${g.id}`} data-testid={`ctc-assign-${g.id}`}
                            className="p-1.5 bg-blue-50 hover:bg-blue-100 rounded text-blue-600" title="Assign employees to this group">
                            <Users className="w-3.5 h-3.5" />
                          </Link>
                          <button data-testid={`ctc-edit-${g.id}`}
                            onClick={() => openEditGroup(g)}
                            className="p-1.5 bg-emerald-50 hover:bg-emerald-100 rounded text-emerald-700" title="Edit this group">
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button data-testid={`ctc-copy-${g.id}`}
                            onClick={() => { setCopySource(g); setShowCopyGroup(true); setShowNewGroup(false); setCopyForm({ new_code: '', new_name: `${g.name} (Copy)`, effective_from: '' }); }}
                            className="p-1.5 bg-slate-100 hover:bg-slate-200 rounded text-slate-600" title="Copy this group">
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                          <button data-testid={`ctc-delete-${g.id}`}
                            onClick={() => handleDeleteGroup(g)}
                            className="p-1.5 bg-rose-50 hover:bg-rose-100 rounded text-rose-600"
                            title={g.employee_count > 0
                              ? `Delete blocked — ${g.employee_count} employee(s) assigned. Reassign first.`
                              : 'Delete this group'}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 mb-2">{g.description || 'No description'}</p>
                      <div className="flex flex-wrap gap-1 items-center justify-between text-xs mb-2">
                        <span className="text-slate-600">{g.employee_count} employees assigned</span>
                        <div className="flex gap-1 items-center">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${g.salary_proration_method==='BASE_DAYS'?'bg-indigo-50 text-indigo-700 border border-indigo-200':'bg-blue-50 text-blue-700 border border-blue-200'}`}>
                            F&F: {g.salary_proration_method==='BASE_DAYS' ? 'Base Days' : 'Wage Days'}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${g.status==='active'?'bg-green-50 text-green-700':'bg-slate-50 text-slate-600'}`}>{g.status}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-slate-400">
                        <Calendar className="w-3 h-3" />
                        <span>Effective: {g.effective_from || 'Not set'}</span>
                        {g.copied_from && <span className="text-purple-500">(copied)</span>}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {/* Stage 73 — show Gross or CTC anchor depending on calc_method.
                            Legacy ctc_based groups get a tooltip nudging migration. */}
                        {(g.calc_method || 'gross_based') === 'gross_based' && Number(g.gross_amount) > 0 && (
                          <span className="text-[9px] bg-emerald-100 text-emerald-800 border border-emerald-300 px-1.5 py-0.5 rounded font-semibold"
                                data-testid={`ctc-gross-${g.id}`}
                                title="Annual Gross — drives % calculations">
                            💰 Gross: ₹{Number(g.gross_amount).toLocaleString('en-IN')}
                          </span>
                        )}
                        {(g.calc_method || 'gross_based') !== 'gross_based' && Number(g.ctc_amount) > 0 && (
                          <span className="text-[9px] bg-blue-100 text-blue-800 border border-blue-300 px-1.5 py-0.5 rounded font-semibold"
                                data-testid={`ctc-amount-${g.id}`}
                                title="Annual CTC anchor — drives % calculations (legacy ctc_based mode)">
                            💰 CTC: ₹{Number(g.ctc_amount).toLocaleString('en-IN')}
                          </span>
                        )}
                        {(g.calc_method || 'gross_based') === 'ctc_based' && (
                          <button onClick={() => openEditGroup(g)}
                            className="text-[9px] bg-amber-50 text-amber-700 border border-amber-300 px-1.5 py-0.5 rounded font-semibold hover:bg-amber-100"
                            data-testid={`ctc-convert-${g.id}`}
                            title="This group uses the legacy CTC-based calc. Click to open in editor and switch to Gross-based.">
                            🔁 Convert to Gross-based
                          </button>
                        )}
                        <span className="text-[9px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded font-semibold">
                          📅 Base: {g.base_days_method === 'fixed' ? `${g.base_days_fixed || 30}d (fixed)` : (g.base_days_method || 'fixed')}
                        </span>
                        {g.ot_enabled && (
                          <span className="text-[9px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded font-semibold">
                            ⏱️ OT: {g.ot_numerator || 'BASIC'} ÷ {g.ot_divisor || 'BASE_DAYS'} × {g.ot_multiplier || 2}×
                          </span>
                        )}
                      </div>
                      {(g.include_food || g.include_gratuity) && (
                        <div className="flex gap-1.5 mt-2">
                          {g.include_food && <span className="text-[9px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded font-semibold">FOOD</span>}
                          {g.include_gratuity && <span className="text-[9px] bg-green-50 text-green-700 border border-green-200 px-1.5 py-0.5 rounded font-semibold">GRATUITY</span>}
                        </div>
                      )}
                      {g.statutory_benefits && (
                        <div className="mt-2 border-t border-purple-100 pt-2 bg-purple-50/50 rounded p-1.5" data-testid={`ctc-stat-summary-${g.id}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] font-semibold text-purple-900 flex items-center gap-1">
                              🛡️ Statutory Benefits Configuration
                            </span>
                            <button
                              onClick={() => openEditGroup(g)}
                              className="text-[9px] font-medium text-purple-700 hover:text-purple-900 underline"
                            >
                              Configure
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {['gratuity', 'bonus', 'leave_encashment'].map(bKey => {
                              const bCfg = g.statutory_benefits[bKey];
                              const isElig = bCfg && bCfg.is_eligible;
                              const bLabel = bKey === 'gratuity' ? 'Gratuity' : (bKey === 'bonus' ? 'Bonus' : 'Leave Enc.');
                              const payer = (bCfg?.payment_responsibility === 'CLIENT_COMPANY_PAYS') ? 'Client Co.' : 'Company';
                              return (
                                <span key={bKey}
                                  className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${
                                    isElig
                                      ? 'bg-purple-100 text-purple-800 border border-purple-200'
                                      : 'bg-slate-100 text-slate-400 border border-slate-200 line-through opacity-70'
                                  }`}
                                  title={isElig ? `${bLabel}: ${bCfg.frequency || 'annual'}, Mode: ${bCfg.amount_mode || 'auto'}, Payer: ${payer}` : `${bLabel}: Not Configured/Disabled`}>
                                  {isElig ? `✓ ${bLabel} (${payer})` : `✕ ${bLabel}`}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {g.salary_heads?.length > 0 && (
                        <div className="mt-2 border-t border-slate-100 pt-2">
                          <p className="text-[10px] text-slate-500 mb-1">Salary Heads:</p>
                          <div className="flex flex-wrap gap-1">
                            {g.salary_heads.map((h, i) => (
                              <span key={i} className="text-[9px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{h.code || h.head_code || h.name}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {ctcGroups.length === 0 && !showNewGroup && (
                    <div className="col-span-full bg-white border border-slate-200 rounded-lg p-8 text-center">
                      <Layers className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">No CTC groups yet</p>
                      <p className="text-xs text-slate-400 mt-1">Create groups like Management, Staff, Worker with different salary structures</p>
                    </div>
                  )}
                </div>

                {/* Add / Edit CTC Group Modal — isolated component for stable inputs */}
                {showNewGroup && (
                  <CTCGroupEditor
                    saving={saving}
                    salaryHeadMaster={salaryHeadMaster}
                    statutoryConfig={statutoryConfig}
                    initial={editingGroup}
                    onCancel={() => { setShowNewGroup(false); setEditingGroup(null); }}
                    onCreate={handleCreateGroup}
                  />
                )}

                {/* Copy Group Modal */}
                {showCopyGroup && copySource && (
                  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setShowCopyGroup(false); setCopySource(null); }}>
                  <div className="bg-white rounded-xl border-2 border-purple-200 w-full max-w-2xl p-5 space-y-4 shadow-2xl" data-testid="ctc-copy-group-form" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-sm text-purple-800 flex items-center gap-2"><Copy className="w-4 h-4" /> Copy from "{copySource.name}" ({copySource.code})</h4>
                      <button onClick={() => { setShowCopyGroup(false); setCopySource(null); }} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
                    </div>
                    <p className="text-xs text-slate-500">All salary heads and settings will be copied. Just provide new code, name, and effective date.</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[10px] font-medium text-slate-600 mb-1">New Group Name *</label>
                        <input data-testid="ctc-copy-name" value={copyForm.new_name} onChange={e=>setCopyForm({...copyForm,new_name:e.target.value})} className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-slate-600 mb-1">New Code *</label>
                        <input data-testid="ctc-copy-code" value={copyForm.new_code} onChange={e=>setCopyForm({...copyForm,new_code:e.target.value.toUpperCase()})} placeholder="e.g. MGMT_V2" className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm font-mono" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-slate-600 mb-1">Effective From *</label>
                        <input data-testid="ctc-copy-effective" type="date" value={copyForm.effective_from} onChange={e=>setCopyForm({...copyForm,effective_from:e.target.value})} className="w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm" />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button data-testid="ctc-copy-btn" onClick={handleCopyGroup} disabled={saving}
                        className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-xs font-medium disabled:opacity-50">
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />} Copy Group
                      </button>
                      <button onClick={() => { setShowCopyGroup(false); setCopySource(null); }} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-xs font-medium">Cancel</button>
                    </div>
                  </div>
                  </div>
                )}

                {/* Auto-Detect Calc Method Modal */}
                {showAutoDetect && (
                  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                       onClick={() => { if (!autoDetectLoading) { setShowAutoDetect(false); setAutoDetectResult(null); } }}>
                    <div className="bg-white rounded-xl border-2 border-amber-200 w-full max-w-4xl p-5 space-y-4 shadow-2xl max-h-[85vh] overflow-y-auto"
                         data-testid="ctc-auto-detect-modal" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-sm text-amber-800 flex items-center gap-2">
                          🪄 Auto-Detect Salary Calculation Methods
                        </h4>
                        <button onClick={() => { setShowAutoDetect(false); setAutoDetectResult(null); }}
                          className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
                      </div>
                      <p className="text-xs text-slate-600">
                        Scans every CTC group's <span className="font-mono">parent_group / code / name</span> for keywords and proposes
                        <span className="font-medium"> Staff (CTC-based)</span> or <span className="font-medium">O&amp;M (Per-Day Wage)</span>.
                        Review the table, then click <span className="font-semibold">Apply</span> to commit.
                      </p>

                      {!autoDetectResult && (
                        <div className="flex items-center gap-3">
                          <label className="flex items-center gap-2 text-xs text-slate-700">
                            <input type="checkbox" checked={autoDetectOverwrite}
                                   onChange={(e) => setAutoDetectOverwrite(e.target.checked)}
                                   data-testid="ctc-auto-detect-overwrite" />
                            Overwrite groups whose method was already explicitly set
                          </label>
                          <button data-testid="ctc-auto-detect-scan-btn"
                            disabled={autoDetectLoading || !selectedCompany}
                            onClick={async () => {
                              setAutoDetectLoading(true);
                              try {
                                const { data } = await axios.post(
                                  `${API_URL}/controls/ctc-groups/auto-detect-methods`,
                                  { company_id: selectedCompany, dry_run: true, overwrite_existing: autoDetectOverwrite },
                                  { withCredentials: true },
                                );
                                setAutoDetectResult(data);
                              } catch (err) {
                                toast.error(formatApiError(err) || 'Auto-detect failed');
                              } finally {
                                setAutoDetectLoading(false);
                              }
                            }}
                            className="px-4 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-xs font-medium disabled:opacity-50">
                            {autoDetectLoading ? 'Scanning…' : 'Scan all groups'}
                          </button>
                        </div>
                      )}

                      {autoDetectResult && (
                        <>
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs" data-testid="ctc-auto-detect-summary">
                            <div className="bg-slate-50 rounded-md p-2 text-center">
                              <div className="text-[10px] uppercase text-slate-500">Total</div>
                              <div className="font-semibold text-slate-800">{autoDetectResult.summary.total_groups}</div>
                            </div>
                            <div className="bg-blue-50 rounded-md p-2 text-center">
                              <div className="text-[10px] uppercase text-blue-700">CTC-based</div>
                              <div className="font-semibold text-blue-800">{autoDetectResult.summary.ctc_based}</div>
                            </div>
                            <div className="bg-emerald-50 rounded-md p-2 text-center">
                              <div className="text-[10px] uppercase text-emerald-700">Per-Day Wage</div>
                              <div className="font-semibold text-emerald-800">{autoDetectResult.summary.per_day_wage}</div>
                            </div>
                            <div className="bg-slate-100 rounded-md p-2 text-center">
                              <div className="text-[10px] uppercase text-slate-500">No change</div>
                              <div className="font-semibold text-slate-700">{autoDetectResult.summary.no_change}</div>
                            </div>
                            <div className="bg-amber-50 rounded-md p-2 text-center">
                              <div className="text-[10px] uppercase text-amber-700">Will Update</div>
                              <div className="font-semibold text-amber-800">{autoDetectResult.summary.will_update}</div>
                            </div>
                          </div>

                          <div className="overflow-x-auto border border-slate-200 rounded-md max-h-96 overflow-y-auto">
                            <table className="w-full text-xs" data-testid="ctc-auto-detect-table">
                              <thead className="bg-slate-50 sticky top-0">
                                <tr>
                                  <th className="px-2 py-1.5 text-left">Code</th>
                                  <th className="px-2 py-1.5 text-left">Name</th>
                                  <th className="px-2 py-1.5 text-left">Parent</th>
                                  <th className="px-2 py-1.5 text-center">Current</th>
                                  <th className="px-2 py-1.5 text-center">Suggested</th>
                                  <th className="px-2 py-1.5 text-left">Reason</th>
                                </tr>
                              </thead>
                              <tbody>
                                {autoDetectResult.proposals.map((p) => (
                                  <tr key={p.group_id}
                                      className={p.will_update ? 'bg-amber-50' : ''}
                                      data-testid={`ctc-auto-detect-row-${p.code}`}>
                                    <td className="px-2 py-1 font-mono text-slate-700">{p.code}</td>
                                    <td className="px-2 py-1 text-slate-700">{p.name}</td>
                                    <td className="px-2 py-1 text-slate-500">{p.parent_group || '—'}</td>
                                    <td className="px-2 py-1 text-center">
                                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                        p.current_method === 'per_day_wage' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'
                                      }`}>{p.current_method === 'per_day_wage' ? 'Per-Day' : 'CTC'}</span>
                                    </td>
                                    <td className="px-2 py-1 text-center">
                                      {p.suggested_method === 'no_change' ? (
                                        <span className="text-slate-400 text-[10px]">no change</span>
                                      ) : (
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                          p.suggested_method === 'per_day_wage' ? 'bg-emerald-200 text-emerald-900' : 'bg-blue-200 text-blue-900'
                                        }`}>{p.suggested_method === 'per_day_wage' ? 'Per-Day' : 'CTC'}</span>
                                      )}
                                    </td>
                                    <td className="px-2 py-1 text-slate-500 text-[11px]">
                                      {p.skipped_existing && <span className="text-amber-700 mr-1">⚠ skipped</span>}
                                      {p.reason}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
                            <button onClick={() => { setShowAutoDetect(false); setAutoDetectResult(null); }}
                              className="px-4 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-xs font-medium">
                              Cancel
                            </button>
                            <button data-testid="ctc-auto-detect-apply-btn"
                              disabled={autoDetectLoading || autoDetectResult.summary.will_update === 0}
                              onClick={async () => {
                                if (!window.confirm(`Apply auto-detect to ${autoDetectResult.summary.will_update} group(s)?`)) return;
                                setAutoDetectLoading(true);
                                try {
                                  const { data } = await axios.post(
                                    `${API_URL}/controls/ctc-groups/auto-detect-methods`,
                                    { company_id: selectedCompany, dry_run: false, overwrite_existing: autoDetectOverwrite },
                                    { withCredentials: true },
                                  );
                                  toast.success(`Updated ${data.updated} group(s)`);
                                  setShowAutoDetect(false);
                                  setAutoDetectResult(null);
                                  fetchTab();
                                } catch (err) {
                                  toast.error(formatApiError(err) || 'Apply failed');
                                } finally {
                                  setAutoDetectLoading(false);
                                }
                              }}
                              className="px-4 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-xs font-medium disabled:opacity-50">
                              {autoDetectLoading ? 'Applying…' : `Apply ${autoDetectResult.summary.will_update} update(s)`}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ====== MONTHLY INPUTS (REDESIGNED GRID) ====== */}
            {tab === 'inputs' && (
              <div className="space-y-4" data-testid="controls-inputs">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-semibold text-sm text-slate-800">Monthly Payroll Input — {period}</h3>
                  <div className="flex gap-2 flex-wrap">
                    <button data-testid="inputs-download-template" onClick={handleDownloadTemplate}
                      className="flex items-center gap-1 px-3 py-1.5 bg-slate-600 hover:bg-slate-700 text-white rounded-md text-xs font-medium">
                      <FileInput className="w-3 h-3"/> Download Template
                    </button>
                    <input ref={inputFileRef} type="file" accept=".csv" className="hidden" data-testid="inputs-file-input"
                      onChange={(e) => { if (e.target.files[0]) handleUploadTemplate(e.target.files[0]); }} />
                    <button data-testid="inputs-upload-btn" onClick={() => inputFileRef.current?.click()} disabled={uploadingFile}
                      className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-xs font-medium disabled:opacity-50">
                      {uploadingFile ? <Loader2 className="w-3 h-3 animate-spin"/> : <Send className="w-3 h-3"/>} Upload Filled Template
                    </button>
                    <button data-testid="inputs-save-btn" onClick={saveInputGrid} disabled={saving}
                      className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-medium disabled:opacity-50">
                      {saving?<Loader2 className="w-3 h-3 animate-spin"/>:<Save className="w-3 h-3"/>} Save
                    </button>
                    <button data-testid="inputs-submit-btn" onClick={()=>doInputAction('submit')} className="flex items-center gap-1 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-xs font-medium"><Send className="w-3 h-3"/> Submit</button>
                    <button data-testid="inputs-bulk-approve-btn" onClick={handleBulkApprove} className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md text-xs font-medium"><CheckCircle2 className="w-3 h-3"/> Bulk Approve</button>
                    <button data-testid="inputs-lock-btn" onClick={()=>doInputAction('lock')} className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md text-xs font-medium"><Lock className="w-3 h-3"/> Lock</button>
                  </div>
                </div>

                {/* Upload result banner */}
                {uploadResult && (
                  <div className={`p-3 rounded-md text-xs ${uploadResult.error_count > 0 ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-green-50 border border-green-200 text-green-800'}`} data-testid="upload-result-banner">
                    <strong>{uploadResult.success_count}</strong> rows saved, <strong>{uploadResult.error_count}</strong> errors.
                    {uploadResult.errors?.slice(0, 5).map((e, i) => <span key={i} className="block mt-0.5">Row {e.row}: {e.error} ({e.code})</span>)}
                    <button onClick={() => setUploadResult(null)} className="ml-2 underline text-xs">Dismiss</button>
                  </div>
                )}

                {inputGrid && (
                  <>
                    {/* Summary badges */}
                    <div className="flex flex-wrap gap-2" data-testid="input-summary-badges">
                      <span className="text-xs bg-slate-100 text-slate-700 px-2.5 py-1 rounded-full font-medium">{inputGrid.total_employees} Employees</span>
                      <button onClick={()=>setInputFilter(inputFilter==='joinee'?'all':'joinee')} className={`text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1 ${inputFilter==='joinee'?'bg-emerald-600 text-white':'bg-emerald-50 text-emerald-700'}`} data-testid="filter-joinee">
                        <UserPlus className="w-3 h-3"/> {inputGrid.joinee_count} Joiners
                      </button>
                      <button onClick={()=>setInputFilter(inputFilter==='increment'?'all':'increment')} className={`text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1 ${inputFilter==='increment'?'bg-blue-600 text-white':'bg-blue-50 text-blue-700'}`} data-testid="filter-increment">
                        <TrendingUp className="w-3 h-3"/> {inputGrid.increment_count} Increments
                      </button>
                      <button onClick={()=>setInputFilter(inputFilter==='resignee'?'all':'resignee')} className={`text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1 ${inputFilter==='resignee'?'bg-red-600 text-white':'bg-red-50 text-red-700'}`} data-testid="filter-resignee">
                        <UserMinus className="w-3 h-3"/> {inputGrid.resignee_count} Resignees
                      </button>
                      <button onClick={()=>setInputFilter(inputFilter==='hold'?'all':'hold')} className={`text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1 ${inputFilter==='hold'?'bg-amber-600 text-white':'bg-amber-50 text-amber-700'}`} data-testid="filter-hold">
                        <Pause className="w-3 h-3"/> {inputGrid.hold_count} On Hold
                      </button>
                      {inputFilter!=='all'&&<button onClick={()=>setInputFilter('all')} className="text-xs text-slate-500 underline">Clear filter</button>}
                    </div>

                    {/* Stage 73.8 — LOP override hint */}
                    <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-[11px] text-amber-900 flex items-start gap-2" data-testid="input-lop-override-banner">
                      <span className="font-semibold">💡 LOP override (fallback):</span>
                      <span>The <b>LOP ovr</b> column lets you manually set LOP days for this period — useful when attendance data is incomplete or being corrected. Leave blank for the engine to use the attendance auto-calc. The attendance/leave engines themselves are NOT modified.</span>
                    </div>

                    {/* Workflow info */}
                    <div className="bg-blue-50 border border-blue-200 rounded-md px-3 py-2 text-xs text-blue-700" data-testid="input-workflow-info">
                      <strong>Workflow:</strong> Download Template → Fill variable data → Upload → Validate → Submit → Approve (Bulk/Individual) → Lock
                      <span className="block mt-1 text-blue-600">
                        💡 <strong>Single Source of Truth:</strong> Saving here automatically updates <strong>Payroll → Monthly Inputs</strong> for the same Company &amp; Period. No manual sync required.
                      </span>
                    </div>

                    {/* Grid table */}
                    <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
                      <table className="w-full text-[11px]" data-testid="monthly-input-grid-table">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-2 py-2 text-left font-semibold text-slate-600 sticky left-0 bg-slate-50 z-10 min-w-[140px]">Employee</th>
                            <th className="px-2 py-2 text-left font-semibold text-slate-600 min-w-[60px]">Dept</th>
                            <th className="px-2 py-2 text-right font-semibold text-slate-500 bg-slate-100 min-w-[70px]" title="Auto from CTC Master">Gross</th>
                            <th className="px-2 py-2 text-right font-semibold text-slate-500 bg-slate-100 min-w-[40px]" title="Auto from Attendance">Pres</th>
                            <th className="px-2 py-2 text-right font-semibold text-slate-500 bg-slate-100 min-w-[35px]" title="Auto from Attendance">LOP</th>
                            <th className="px-2 py-2 text-right font-semibold text-amber-700 bg-amber-50 min-w-[60px]" title="Manual LOP override (blank = use auto). When set, this REPLACES the attendance-derived LOP for THIS period only.">LOP ovr</th>
                            <th className="px-2 py-2 text-right font-semibold text-emerald-700 bg-emerald-50 min-w-[60px]" title="Additional days released on top of pay cycle (earnings prorate UP)">Addl Days</th>
                            <th className="px-2 py-2 text-right font-semibold text-blue-700 min-w-[65px]">OT Amt</th>
                            <th className="px-2 py-2 text-right font-semibold text-blue-700 min-w-[70px]">Incentive</th>
                            <th className="px-2 py-2 text-right font-semibold text-blue-700 min-w-[65px]">Arrears</th>
                            <th className="px-2 py-2 text-right font-semibold text-blue-700 min-w-[65px]">Reimb</th>
                            <th className="px-2 py-2 text-right font-semibold text-blue-700 min-w-[65px]">Oth Earn</th>
                            <th className="px-2 py-2 text-right font-semibold text-red-700 min-w-[65px]">Oth Ded</th>
                            {/* Dynamic salary head columns — auto-generated from Masters */}
                            {inputDynHeadDefs.map(hd => (
                              <th key={hd.code}
                                className={`px-2 py-2 text-right font-semibold min-w-[80px] ${
                                  hd.head_type === 'deduction' ? 'bg-rose-50 text-rose-800' : 'bg-violet-50 text-violet-800'
                                }`}
                                title={`${hd.name} — dynamic salary head (${hd.head_type}). Configured in Masters → Salary Heads.`}
                              >
                                {hd.name}
                              </th>
                            ))}
                            <th className="px-2 py-2 text-right font-semibold text-rose-700 bg-rose-50 min-w-[70px]" title="Override the engine-calculated TDS for this month only">TDS Ovr</th>
                            <th className="px-2 py-2 text-center font-semibold text-amber-700 min-w-[45px]">Hold</th>
                            <th className="px-2 py-2 text-left font-semibold text-blue-700 min-w-[100px]">Remarks</th>
                            <th className="px-2 py-2 text-center font-semibold text-slate-600 min-w-[60px]">Status</th>
                            <th className="px-2 py-2 text-center font-semibold text-green-700 min-w-[50px]">Act</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRows.map(r => (
                            <tr key={r.employee_id} className={`border-b border-slate-50 hover:bg-blue-50/30 ${r.hold_salary ? 'bg-amber-50/50' : ''}`}>
                              <td className="px-2 py-1.5 sticky left-0 bg-white z-10">
                                <div className="flex items-center gap-1">
                                  {r.employee_code && !/^[0-9a-fA-F]{24}$/.test(r.employee_code) && <span className="font-mono text-slate-600">{r.employee_code}</span>}
                                  <span className="text-slate-800 truncate max-w-[80px]">{r.employee_name && !/^[0-9a-fA-F]{24}$/.test(r.employee_name) ? r.employee_name.split(' ')[0] : 'Unknown'}</span>
                                  {r.is_new_joinee && <span className="px-1 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[8px] font-bold" title={`Joined ${r.doj}`}>NEW</span>}
                                  {r.has_increment && <span className="px-1 py-0.5 bg-blue-100 text-blue-700 rounded text-[8px] font-bold">INC</span>}
                                  {r.is_resignee && <span className="px-1 py-0.5 bg-red-100 text-red-700 rounded text-[8px] font-bold" title={`LWD ${r.lwd}`}>EXIT</span>}
                                </div>
                              </td>
                              <td className="px-2 py-1.5 text-slate-500">{r.department}</td>
                              <td className="px-2 py-1.5 text-right font-mono text-slate-400 bg-slate-50">{(r.gross_monthly||0).toLocaleString()}</td>
                              <td className="px-2 py-1.5 text-right font-mono text-slate-400 bg-slate-50">{r.present_days}</td>
                              <td className="px-2 py-1.5 text-right font-mono bg-slate-50"><span className={r.lop_days>0?'text-red-500 font-semibold':'text-slate-400'}>{r.lop_days}</span></td>
                              {/* Stage 73.8 — LOP override editable cell.
                                  Blank = engine uses attendance auto-calc.
                                  Number = engine uses this exact LOP for the period. */}
                              <td className={`px-2 py-1 ${r.lop_days_override != null && r.lop_days_override !== '' ? 'bg-amber-100' : ''}`}>
                                <input type="number" step="0.5" min="0" placeholder="auto"
                                  value={r.lop_days_override ?? ''}
                                  onChange={e => updateInputRow(r.employee_id, 'lop_days_override', e.target.value === '' ? null : parseFloat(e.target.value) || 0)}
                                  className="w-full text-right border border-amber-200 rounded px-1 py-0.5 text-[11px] font-mono bg-white focus:ring-1 focus:ring-amber-400 placeholder:text-slate-300"
                                  title={r.lop_days_override != null && r.lop_days_override !== ''
                                    ? `Manual override: ${r.lop_days_override} days will REPLACE attendance auto-calc (${r.lop_days} d) for this period.`
                                    : 'Blank = use attendance auto. Type a number to override.'}
                                  data-testid={`input-lop-override-${r.employee_code}`} />
                              </td>
                              {/* Additional Days */}
                              <td className={`px-2 py-1 ${r.additional_days != null && r.additional_days !== '' && Number(r.additional_days) > 0 ? 'bg-emerald-50' : ''}`}>
                                <input type="number" step="0.5" min="0" placeholder="0"
                                  value={r.additional_days ?? ''}
                                  onChange={e => updateInputRow(r.employee_id, 'additional_days', e.target.value === '' ? null : parseFloat(e.target.value) || 0)}
                                  className="w-full text-right border border-emerald-200 rounded px-1 py-0.5 text-[11px] font-mono focus:ring-1 focus:ring-emerald-400 placeholder:text-slate-300"
                                  title="Additional days released on top of pay cycle (earnings prorate UP)"
                                  data-testid={`input-addl-days-${r.employee_code}`} />
                              </td>
                              <td className="px-2 py-1"><input type="number" value={r.ot_amount||''} onChange={e=>updateInputRow(r.employee_id,'ot_amount',parseFloat(e.target.value)||0)} className="w-full text-right border border-slate-200 rounded px-1 py-0.5 text-[11px] font-mono focus:ring-1 focus:ring-blue-400" data-testid={`input-ot-${r.employee_code}`}/></td>
                              <td className="px-2 py-1"><input type="number" value={r.incentive||''} onChange={e=>updateInputRow(r.employee_id,'incentive',parseFloat(e.target.value)||0)} className="w-full text-right border border-slate-200 rounded px-1 py-0.5 text-[11px] font-mono focus:ring-1 focus:ring-blue-400" data-testid={`input-inc-${r.employee_code}`}/></td>
                              <td className="px-2 py-1"><input type="number" value={r.arrears||''} onChange={e=>updateInputRow(r.employee_id,'arrears',parseFloat(e.target.value)||0)} className="w-full text-right border border-slate-200 rounded px-1 py-0.5 text-[11px] font-mono focus:ring-1 focus:ring-blue-400" data-testid={`input-arr-${r.employee_code}`}/></td>
                              <td className="px-2 py-1"><input type="number" value={r.reimbursement||''} onChange={e=>updateInputRow(r.employee_id,'reimbursement',parseFloat(e.target.value)||0)} className="w-full text-right border border-slate-200 rounded px-1 py-0.5 text-[11px] font-mono focus:ring-1 focus:ring-blue-400" data-testid={`input-reimb-${r.employee_code}`}/></td>
                              <td className="px-2 py-1"><input type="number" value={r.other_earning||''} onChange={e=>updateInputRow(r.employee_id,'other_earning',parseFloat(e.target.value)||0)} className="w-full text-right border border-slate-200 rounded px-1 py-0.5 text-[11px] font-mono focus:ring-1 focus:ring-blue-400" data-testid={`input-othern-${r.employee_code}`}/></td>
                              <td className="px-2 py-1"><input type="number" value={r.other_deduction||''} onChange={e=>updateInputRow(r.employee_id,'other_deduction',parseFloat(e.target.value)||0)} className="w-full text-right border border-red-200 rounded px-1 py-0.5 text-[11px] font-mono focus:ring-1 focus:ring-red-400" data-testid={`input-ded-${r.employee_code}`}/></td>
                              {/* Dynamic salary head value cells — one per custom head */}
                              {inputDynHeadDefs.map(hd => (
                                <td key={hd.code} className={`px-2 py-1 ${
                                  (r.dynamic_heads || {})[hd.code] && Number((r.dynamic_heads || {})[hd.code]) !== 0
                                    ? (hd.head_type === 'deduction' ? 'bg-rose-50/60' : 'bg-violet-50/60')
                                    : ''
                                }`}>
                                  <input
                                    type="number" step="0.01"
                                    min={hd.allow_negative ? undefined : 0}
                                    value={(r.dynamic_heads || {})[hd.code] ?? 0}
                                    onChange={e => updateInputRow(r.employee_id, `dynamic_heads.${hd.code}`, parseFloat(e.target.value) || 0)}
                                    className={`w-full text-right border rounded px-1 py-0.5 text-[11px] font-mono focus:ring-1 ${
                                      hd.head_type === 'deduction'
                                        ? 'border-rose-200 focus:ring-rose-400'
                                        : 'border-violet-200 focus:ring-violet-400'
                                    }`}
                                    data-testid={`input-dyn-${hd.code}-${r.employee_code}`}
                                  />
                                </td>
                              ))}
                              {/* TDS Override */}
                              <td className={`px-2 py-1 ${r.tds_override != null && r.tds_override !== '' ? 'bg-rose-50' : ''}`}>
                                <input type="number" step="1" min="0"
                                  placeholder={r.calculated_tds != null ? String(r.calculated_tds) : 'auto'}
                                  value={r.tds_override ?? ''}
                                  onChange={e => updateInputRow(r.employee_id, 'tds_override', e.target.value === '' ? null : Math.max(0, parseFloat(e.target.value) || 0))}
                                  className="w-full text-right border border-rose-200 rounded px-1 py-0.5 text-[11px] font-mono focus:ring-1 focus:ring-rose-400 placeholder:text-slate-300 placeholder:italic"
                                  title="Override the engine-calculated TDS for this month only."
                                  data-testid={`input-tds-override-${r.employee_code}`} />
                              </td>
                              <td className="px-2 py-1 text-center"><input type="checkbox" checked={r.hold_salary||false} onChange={e=>updateInputRow(r.employee_id,'hold_salary',e.target.checked)} className="rounded border-slate-300" data-testid={`input-hold-${r.employee_code}`}/></td>
                              <td className="px-2 py-1"><input type="text" value={r.remarks||''} onChange={e=>updateInputRow(r.employee_id,'remarks',e.target.value)} placeholder="..." className="w-full border border-slate-200 rounded px-1 py-0.5 text-[11px] focus:ring-1 focus:ring-blue-400" data-testid={`input-remarks-${r.employee_code}`}/></td>
                              <td className="px-2 py-1 text-center">
                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                                  r.input_status === 'approved' ? 'bg-green-50 text-green-700' :
                                  r.input_status === 'pending_approval' ? 'bg-amber-50 text-amber-700' :
                                  r.input_status === 'locked' ? 'bg-red-50 text-red-700' :
                                  'bg-slate-50 text-slate-500'
                                }`}>{r.input_status === 'no_data' ? 'Draft' : r.input_status}</span>
                              </td>
                              <td className="px-2 py-1 text-center">
                                {r.input_status === 'pending_approval' && (
                                  <button onClick={() => handleIndividualApprove(r.employee_id)}
                                    className="px-1.5 py-0.5 bg-green-100 hover:bg-green-200 text-green-800 rounded text-[9px] font-semibold"
                                    data-testid={`input-approve-${r.employee_code}`}>
                                    Approve
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {filteredRows.length === 0 && <p className="text-xs text-slate-400 text-center py-6">No employees match the filter</p>}
                    </div>

                    {/* How joiners/increments/resignees are handled */}
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2" data-testid="input-rules-info">
                      <h4 className="text-xs font-semibold text-slate-700">How special cases are handled:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-2.5">
                          <p className="font-semibold text-emerald-800 flex items-center gap-1"><UserPlus className="w-3.5 h-3.5"/> New Joinee</p>
                          <p className="text-emerald-700 mt-1">Auto-detected from DOJ. CTC auto-flows. Salary prorated by engine. No manual input for fixed.</p>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-2.5">
                          <p className="font-semibold text-blue-800 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5"/> Increment</p>
                          <p className="text-blue-700 mt-1">Flagged when new CTC effective. Arrears in Arrears column. Engine picks latest CTC.</p>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded-md p-2.5">
                          <p className="font-semibold text-red-800 flex items-center gap-1"><UserMinus className="w-3.5 h-3.5"/> Resignee</p>
                          <p className="text-red-700 mt-1">Auto-detected from LWD. Salary prorated. Hold for FnF. Oth Ded for notice.</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ====== ATTENDANCE CONTROL ====== */}
            {tab === 'attendance' && (
              <div className="space-y-4" data-testid="controls-attendance">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-sm text-slate-800">Attendance Settings (Configuration only)</h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      For uploads/imports/internal entry/calculation, use <a href="/attendance/upload" className="text-blue-600 hover:underline">Attendance → Operations</a>.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button data-testid="att-lock-btn" onClick={()=>doAttLock('lock')} className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md text-xs font-medium"><Lock className="w-3 h-3"/> Lock Period</button>
                    <button data-testid="att-unlock-btn" onClick={()=>doAttLock('unlock')} className="flex items-center gap-1 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-xs font-medium"><Unlock className="w-3 h-3"/> Unlock</button>
                    <button data-testid="att-save-btn" onClick={saveAttConfig} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-medium disabled:opacity-50">{saving?<Loader2 className="w-3 h-3 animate-spin"/>:<Save className="w-3 h-3"/>} Save Settings</button>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 flex items-start gap-2">
                  <span>💡</span>
                  <div>
                    <b>Settings are split as follows:</b><br/>
                    • <b>Default Source & Sources Master</b> — choose how attendance flows in.<br/>
                    • <b>Calculation Rules</b> — present / half-day / late / OT thresholds + auto-mark toggles.<br/>
                    For day-to-day uploads & calculation runs, go to <a href="/attendance/upload" className="text-blue-700 underline">Attendance → Upload / Import</a>.
                  </div>
                </div>

                {attConfig && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
                      <h4 className="text-xs font-semibold text-slate-700 uppercase">Default Attendance Source</h4>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-slate-600 w-32">Source</label>
                        <select value={attConfig.source||''} onChange={e=>setAttConfig({...attConfig, source:e.target.value})} className="flex-1 border border-slate-300 rounded px-2 py-1 text-xs" data-testid="att-source">
                          <option value="manual_upload">Manual Upload</option>
                          <option value="biometric">Biometric</option>
                          <option value="elite">Elite Integration</option>
                          <option value="api">API</option>
                          <option value="internal">Internal</option>
                        </select>
                      </div>
                      <h4 className="text-xs font-semibold text-slate-700 uppercase pt-2 border-t border-slate-100">Calculation Rules</h4>
                      {[
                        {k:'missing_punch_rule',l:'Missing Punch',t:'select',o:['mark_absent','mark_present','exception']},
                        {k:'late_threshold_minutes',l:'Late (min)',t:'number'},
                        {k:'half_day_threshold_hours',l:'Half-day (hrs)',t:'number'},
                        {k:'full_day_threshold_hours',l:'Full-day (hrs)',t:'number'},
                        {k:'ot_threshold_hours',l:'OT (hrs)',t:'number'},
                      ].map(f=>(
                        <div key={f.k} className="flex items-center gap-2">
                          <label className="text-xs text-slate-600 w-32">{f.l}</label>
                          {f.t==='select'?<select value={attConfig[f.k]||''} onChange={e=>setAttConfig({...attConfig,[f.k]:e.target.value})} className="flex-1 border border-slate-300 rounded px-2 py-1 text-xs" data-testid={`att-${f.k}`}>{f.o.map(o=><option key={o} value={o}>{o}</option>)}</select>
                          :<input type="number" value={attConfig[f.k]||0} onChange={e=>setAttConfig({...attConfig,[f.k]:parseFloat(e.target.value)})} className="w-24 border border-slate-300 rounded px-2 py-1 text-xs font-mono" data-testid={`att-${f.k}`}/>}
                        </div>
                      ))}
                    </div>
                    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
                      <h4 className="text-xs font-semibold text-slate-700 uppercase">Auto-mark Toggles</h4>
                      {[{k:'auto_mark_absent',l:'Auto-mark absent'},{k:'auto_mark_weekoff',l:'Auto-mark weekly off'},{k:'auto_mark_holiday',l:'Auto-mark holiday'},{k:'ot_requires_approval',l:'OT requires approval'},{k:'regularization_allowed',l:'Regularization allowed'},{k:'regularization_requires_approval',l:'Regularization needs approval'}].map(({k,l})=>(
                        <label key={k} className="flex items-center gap-2 text-xs text-slate-700"><input type="checkbox" checked={attConfig[k]||false} onChange={e=>setAttConfig({...attConfig,[k]:e.target.checked})} className="rounded border-slate-300" data-testid={`att-${k}`}/>{l}</label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-slate-700 uppercase mb-2">Quick Links</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <a href="/attendance/upload" className="bg-blue-50 hover:bg-blue-100 text-blue-700 rounded p-2 text-center">📤 Upload / Import</a>
                    <a href="/attendance/history" className="bg-slate-50 hover:bg-slate-100 text-slate-700 rounded p-2 text-center">📚 Upload History</a>
                    <a href="/attendance/internal" className="bg-amber-50 hover:bg-amber-100 text-amber-700 rounded p-2 text-center">✏️ Internal Attendance</a>
                    <a href="/attendance/calculation" className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded p-2 text-center">🧮 Calculation</a>
                    <a href="/attendance/biometric/mapping" className="bg-purple-50 hover:bg-purple-100 text-purple-700 rounded p-2 text-center">🔗 Biometric Mapping</a>
                    <a href="/leave-policy" className="bg-teal-50 hover:bg-teal-100 text-teal-700 rounded p-2 text-center">🌴 Leave Policy</a>
                    <a href="/attendance/reports" className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded p-2 text-center">📊 Reports</a>
                    <a href="/attendance/regularization" className="bg-rose-50 hover:bg-rose-100 text-rose-700 rounded p-2 text-center">📝 Regularization</a>
                  </div>
                </div>

                {attRecon && (
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <h4 className="text-xs font-semibold text-slate-700 uppercase mb-3">Reconciliation — {period}</h4>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                      <SC label="Employees" value={attRecon.total_employees}/>
                      <SC label="Expected" value={attRecon.total_expected_records}/>
                      <SC label="Actual" value={attRecon.total_actual_records} color="green"/>
                      <SC label="Missing" value={attRecon.total_missing_records} color="red"/>
                      <SC label="Match %" value={attRecon.match_percentage != null ? `${attRecon.match_percentage}%` : 'N/A'} color={attRecon.match_percentage===100?'green':'amber'}/>
                    </div>
                    {attRecon.exceptions?.length>0&&(
                      <div className="overflow-x-auto max-h-[200px]" data-testid="att-recon-exceptions">
                        <table className="w-full text-xs"><thead><tr className="bg-slate-50"><th className="px-2 py-1.5 text-left">Code</th><th className="px-2 py-1.5 text-left">Name</th><th className="px-2 py-1.5 text-right">Expected</th><th className="px-2 py-1.5 text-right">Actual</th><th className="px-2 py-1.5 text-right">Missing</th></tr></thead>
                        <tbody>{attRecon.exceptions.slice(0,20).map(e=>(<tr key={e.employee_id} className="border-b border-slate-50"><td className="px-2 py-1.5 font-mono">{e.employee_code}</td><td className="px-2 py-1.5">{e.employee_name}</td><td className="px-2 py-1.5 text-right">{e.expected_records}</td><td className="px-2 py-1.5 text-right text-green-600">{e.actual_records}</td><td className="px-2 py-1.5 text-right text-red-600 font-semibold">{e.missing_records}</td></tr>))}</tbody></table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ====== MODULE RECONCILIATION ====== */}
            {tab === 'reconciliation' && (
              <div className="space-y-4" data-testid="controls-reconciliation">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sm text-slate-800 flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4 text-rose-600" /> Pre-Payroll Module Reconciliation — {period}
                  </h3>
                  <button onClick={fetchTab} className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-xs font-medium">
                    Refresh Report
                  </button>
                </div>

                {reconciliationReport && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <SC label="Total Records" value={reconciliationReport.total_records} color="blue" />
                      <SC label="Unresolved Conflicts" value={reconciliationReport.unresolved_conflicts} color={reconciliationReport.unresolved_conflicts > 0 ? 'red' : 'green'} />
                      <SC label="Payroll Lock Status" value={reconciliationReport.can_proceed_payroll ? 'READY' : 'BLOCKED'} color={reconciliationReport.can_proceed_payroll ? 'green' : 'red'} />
                    </div>

                    {!reconciliationReport.can_proceed_payroll && (
                      <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-rose-900 text-xs flex items-start gap-3">
                        <ShieldAlert className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                        <div>
                          <p className="font-bold text-sm">Hard Lock Gate Active!</p>
                          <p className="mt-0.5">
                            {reconciliationReport.unresolved_conflicts} input conflict(s) exist between Business Control Area and Payroll Monthly Input for period {period}. Period lock and payroll run are blocked until these duplicates/mismatches are resolved.
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 font-semibold text-xs text-slate-700">
                        Cross-Module Field Comparison Matrix
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs text-left">
                          <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase text-[10px] tracking-wider">
                            <tr>
                              <th className="py-2.5 px-3">Employee</th>
                              <th className="py-2.5 px-3">Field / Category</th>
                              <th className="py-2.5 px-3 text-right">Business Control Value</th>
                              <th className="py-2.5 px-3">Business Control Status</th>
                              <th className="py-2.5 px-3 text-right">Payroll Input Value</th>
                              <th className="py-2.5 px-3">Payroll Input Status</th>
                              <th className="py-2.5 px-3 text-center">Conflict Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {(reconciliationReport.items || []).map((item, idx) => (
                              <tr key={idx} className={`hover:bg-slate-50 ${item.conflict_status.startsWith('CONFLICT') ? 'bg-rose-50/40' : ''}`}>
                                <td className="py-2.5 px-3 font-medium text-slate-900">
                                  {item.employee_name || '—'}
                                  <div className="text-[10px] text-slate-500 font-mono">{item.employee_code}</div>
                                </td>
                                <td className="py-2.5 px-3 font-semibold text-slate-700 uppercase">{item.field}</td>
                                <td className="py-2.5 px-3 text-right font-mono font-medium text-slate-900">
                                  {item.business_control_value != null ? item.business_control_value : '—'}
                                </td>
                                <td className="py-2.5 px-3 capitalize">{item.business_control_status || '—'}</td>
                                <td className="py-2.5 px-3 text-right font-mono font-medium text-slate-900">
                                  {item.payroll_input_value != null ? item.payroll_input_value : '—'}
                                </td>
                                <td className="py-2.5 px-3 capitalize">{item.payroll_input_status || '—'}</td>
                                <td className="py-2.5 px-3 text-center">
                                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                    item.conflict_status === 'CONFLICT_DUPLICATE' ? 'bg-rose-100 text-rose-800'
                                    : item.conflict_status === 'CONFLICT_VALUE_MISMATCH' ? 'bg-amber-100 text-amber-800'
                                    : item.conflict_status === 'BUSINESS_CONTROL_ONLY' ? 'bg-blue-50 text-blue-700'
                                    : 'bg-emerald-50 text-emerald-700'
                                  }`}>
                                    {item.conflict_status.replace(/_/g, ' ')}
                                  </span>
                                </td>
                              </tr>
                            ))}
                            {(!reconciliationReport.items || reconciliationReport.items.length === 0) && (
                              <tr><td colSpan={7} className="text-center py-8 text-slate-400">No variable inputs recorded in either module for period {period}.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default BusinessControls;
